import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Chip, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { getLoginUrl, getSignupUrl, getSocialLoginUrl } from '../services/auth';
import { AGENCY_TRIAL_CREDITS, isSelfSignupTier, type SelfSignupTier } from '@traaker/shared';
import { applyUiLanguage } from '../i18n';
import { SIGNUP_LANG_STASH_KEY, normalizeUiLanguage } from '../i18n/languages';

/**
 * Public, pre-auth signup entry — two-column split (auth left, marketing right).
 * Pick a plan upstream on the pricing page (?tier=…); here we just authenticate
 * (Google or email via the Cognito hosted UI). The chosen tier + an optional
 * brand-URL hint are stashed so they survive the IdP redirect and are read back
 * by the post-auth /onboarding step (which also offers the tier picker when no
 * tier was preselected).
 *
 * `?lang=fr|en` (handed off by the marketing site's language switcher) picks
 * the page's own language immediately AND is stashed so the very first
 * onboarding screens (before any per-user preference exists) match the
 * language the visitor was already browsing traaker.ai in.
 *
 * Enterprise is admin-provisioned → "Parler à un expert". Agency self-signs up
 * (no-expiry credit trial); its plan is chosen on the pricing page.
 */
const PLAN_LABELS: Record<SelfSignupTier, { labelKey: string; trialKey: string }> = {
  basic: { labelKey: 'signup.plan.basic', trialKey: 'signup.trial.days7' },
  pro: { labelKey: 'signup.plan.pro', trialKey: 'signup.trial.noCommitment' },
  elite: { labelKey: 'signup.plan.elite', trialKey: 'signup.trial.noCommitment' },
  agency: { labelKey: 'signup.plan.agency', trialKey: 'signup.trial.creditsOffered' },
};

const TIER_STASH_KEY = 'traaker_signup_tier';
const BRAND_STASH_KEY = 'traaker_signup_brand';
/**
 * Ad attribution, stashed on the same rail as tier/brand/lang so it survives
 * the Cognito hosted-UI redirect (which drops all query params). Read back by
 * Onboarding.tsx and posted with the tenant. `gclid` is the Google Ads click
 * id — the `_gcl_aw` cookie is NOT readable here (host-only on traaker.ai),
 * and the click id is what offline-conversion import wants anyway.
 *
 * Requires the marketing site to forward these params into
 * app.traaker.ai/signup — without that there's nothing to capture.
 */
const GCLID_STASH_KEY = 'traaker_signup_gclid';
const UTM_SOURCE_STASH_KEY = 'traaker_signup_utm_source';
const UTM_CAMPAIGN_STASH_KEY = 'traaker_signup_utm_campaign';

const ENGINES: { label: string; src: string }[] = [
  { label: 'ChatGPT', src: '/engines/chatgpt.svg' },
  { label: 'Gemini', src: '/engines/gemini.svg' },
  { label: 'Claude', src: '/engines/claude.svg' },
  { label: 'Perplexity', src: '/engines/perplexity.svg' },
  { label: 'Mistral', src: '/engines/mistral.svg' },
  { label: 'Grok', src: '/engines/grok.svg' },
  { label: 'DeepSeek', src: '/engines/deepseek.svg' },
  { label: 'Meta AI', src: '/engines/meta.svg' },
];
const PILLAR_KEYS = [
  'signup.pillar.cockpit',
  'signup.pillar.monitor',
  'signup.pillar.analyze',
  'signup.pillar.act',
  'signup.pillar.optimize',
  'signup.pillar.measure',
];

export default function Signup() {
  const [params] = useSearchParams();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();

  const tierParam = params.get('tier');
  const tier: SelfSignupTier | null =
    tierParam && isSelfSignupTier(tierParam) ? tierParam : null;
  const brandHint = params.get('brand') || '';
  const langParam = params.get('lang');
  const gclid = params.get('gclid') || '';
  const utmSource = params.get('utm_source') || '';
  const utmCampaign = params.get('utm_campaign') || '';

  const [busy, setBusy] = useState(false);

  // Switch this page's own language to match the marketing site immediately
  // (i18n falls back to the browser language otherwise, which may disagree
  // with what the visitor was just reading on traaker.ai).
  useEffect(() => {
    if (langParam) applyUiLanguage(langParam);
  }, [langParam]);

  const stashAndGo = (url: string) => {
    setBusy(true);
    // Persist the plan choice (+ brand hint + language) across the Cognito/IdP redirect.
    // The stash DECIDES the account type post-auth (Onboarding.tsx: agency vs
    // entreprise), so clear it when this visit carries no tier — otherwise an
    // organic signup could inherit a stale agency choice from an earlier visit.
    if (tier) localStorage.setItem(TIER_STASH_KEY, tier);
    else localStorage.removeItem(TIER_STASH_KEY);
    if (brandHint) localStorage.setItem(BRAND_STASH_KEY, brandHint);
    if (langParam) localStorage.setItem(SIGNUP_LANG_STASH_KEY, normalizeUiLanguage(langParam));
    // Ad attribution — only write when present, so a later organic signup in the
    // same browser can't inherit a stale gclid from an earlier ad visit.
    if (gclid) localStorage.setItem(GCLID_STASH_KEY, gclid);
    if (utmSource) localStorage.setItem(UTM_SOURCE_STASH_KEY, utmSource);
    if (utmCampaign) localStorage.setItem(UTM_CAMPAIGN_STASH_KEY, utmCampaign);
    window.location.href = url;
  };

  const state = tier ? `tier:${tier}` : undefined;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      {/* Left — auth */}
      <Box
        sx={{
          flex: wide ? '0 0 46%' : '1 1 100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 3, sm: 6 },
          py: 6,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Box component="img" src="/traaker-logo-compact.png" alt="Traaker" sx={{ height: 32, mb: 6 }} />

          <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.02em', mb: 1 }}>
            {t('signup.createAccount')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('signup.subtitle')}
          </Typography>

          {tier && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={t('signup.planChip', {
                plan: t(PLAN_LABELS[tier].labelKey),
                trial: t(PLAN_LABELS[tier].trialKey, { credits: AGENCY_TRIAL_CREDITS }),
              })}
              sx={{ mb: 3, mt: 1, fontWeight: 600 }}
            />
          )}
          <Box sx={{ mb: 3 }} />

          <Button
            variant="outlined"
            fullWidth
            size="large"
            disabled={busy}
            startIcon={<GoogleMark />}
            onClick={() => stashAndGo(getSocialLoginUrl('Google', state))}
            sx={{ mb: 2, py: 1.25, borderColor: 'divider', color: 'text.primary' }}
          >
            {t('signup.continueGoogle')}
          </Button>

          <Divider sx={{ my: 1.5, color: 'text.secondary', fontSize: 13 }}>{t('signup.or')}</Divider>

          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={busy}
            onClick={() => stashAndGo(getSignupUrl(state))}
            sx={{ py: 1.25 }}
          >
            {t('signup.signUpEmail')}
          </Button>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            {t('signup.businessEmailRequired')}
          </Typography>

          <Stack spacing={1.5} sx={{ mt: 4 }}>
            <Typography variant="caption" color="text.secondary">
              <Trans
                i18nKey="signup.alreadyAccount"
                components={{
                  loginLink: (
                    <a href={getLoginUrl()} style={{ color: theme.palette.primary.main, fontWeight: 600 }} />
                  ),
                }}
              />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <Trans
                i18nKey="signup.needCustomContract"
                components={{
                  contactLink: (
                    <a href="https://traaker.ai/contact.html" style={{ color: theme.palette.primary.main, fontWeight: 600 }} />
                  ),
                }}
              />
            </Typography>
            {/* The post-auth onboarding derives the account type from ?tier=,
                so a visitor landing here without the agency tier would be
                provisioned as entreprise — point agencies at their funnel. */}
            {tier !== 'agency' && (
              <Typography variant="caption" color="text.secondary">
                <Trans
                  i18nKey="signup.agencyHint"
                  components={{
                    agencyLink: (
                      <a href="https://traaker.ai/tarifs-agences.html" style={{ color: theme.palette.primary.main, fontWeight: 600 }} />
                    ),
                  }}
                />
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled">
              <Trans
                i18nKey="signup.termsAgreement"
                components={{
                  termsLink: (
                    <a href="https://traaker.ai/cgv.html" style={{ color: 'inherit', textDecoration: 'underline' }} />
                  ),
                }}
              />
            </Typography>
          </Stack>
        </Box>
      </Box>

      {/* Right — marketing (md+ only) */}
      {wide && <MarketingPanel />}
    </Box>
  );
}

function MarketingPanel() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        flex: '1 1 54%',
        position: 'relative',
        color: 'common.white',
        bgcolor: '#0B0B12',
        backgroundImage:
          'radial-gradient(900px 500px at 85% -10%, rgba(124,58,237,0.28), transparent 60%), ' +
          'linear-gradient(180deg, #0B0B12 0%, #0E0B1A 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: { md: 7, lg: 10 },
        py: 8,
      }}
    >
      <Typography variant="h3" fontWeight={800} sx={{ letterSpacing: '-0.03em', lineHeight: 1.05 }}>
        {t('signup.marketing.headlineLine1')}
        <br />
        {t('signup.marketing.headlineLine2')}
        <Box component="span" sx={{ display: 'block', color: '#A78BFA' }}>
          {t('signup.marketing.headlineHighlight')}
        </Box>
      </Typography>

      <Typography sx={{ mt: 3, maxWidth: 460, color: 'rgba(255,255,255,0.72)' }}>
        {t('signup.marketing.subtitle')}
      </Typography>

      {/* Capability stats — capability, not traction (no customer claims yet). */}
      <Stack direction="row" spacing={5} sx={{ mt: 5 }}>
        {[
          { target: 8, suffix: '', l: t('signup.marketing.stat.engines') },
          { target: 11, suffix: '', l: t('signup.marketing.stat.agents') },
          { target: 240, suffix: '+', l: t('signup.marketing.stat.zones') },
        ].map((s) => (
          <Box key={s.l}>
            <Typography variant="h4" fontWeight={800}>
              <CountUp target={s.target} suffix={s.suffix} />
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>{s.l}</Typography>
          </Box>
        ))}
      </Stack>

      <Stack direction="row" sx={{ mt: 6, flexWrap: 'wrap', gap: 1.5 }}>
        {ENGINES.map((e) => (
          <Box
            key={e.label}
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
            }}
            title={e.label}
          >
            <Box
              component="img"
              src={e.src}
              alt={e.label}
              sx={{ width: 24, height: 24, objectFit: 'contain', display: 'block' }}
            />
          </Box>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 4, flexWrap: 'wrap', gap: 1 }}>
        {PILLAR_KEYS.map((key) => (
          <Chip
            key={key}
            label={t(key)}
            size="small"
            variant="outlined"
            sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.14)' }}
          />
        ))}
      </Stack>
    </Box>
  );
}

/** Animates a number from 0 to `target` once on mount (ease-out, ~1.2s). */
function CountUp({ target, suffix = '', duration = 1200 }: { target: number; suffix?: string; duration?: number }) {
  const [value, setValue] = useState(0);
  const frame = useRef<number | undefined>(undefined);

  useEffect(() => {
    let start: number | null = null;
    const step = (ts: number) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setValue(Math.round(eased * target));
      if (p < 1) frame.current = requestAnimationFrame(step);
    };
    frame.current = requestAnimationFrame(step);
    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [target, duration]);

  return (
    <>
      {value}
      {suffix}
    </>
  );
}

/** Inline Google "G" so we don't pull an icon dependency. */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

export {
  TIER_STASH_KEY,
  BRAND_STASH_KEY,
  GCLID_STASH_KEY,
  UTM_SOURCE_STASH_KEY,
  UTM_CAMPAIGN_STASH_KEY,
};
